import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useInstructorMode } from '../hooks/useInstructorMode';
import { formatShortDate } from '../utils/dateUtils';
import './TopSongs.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function TopSongs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { buildUrl, isInstructorMode } = useInstructorMode();
  
  // Filter states for instructor
  const [hideRecent, setHideRecent] = useState(false);
  const [hideAfterColorado, setHideAfterColorado] = useState(false);
  const [showOneHitWonders, setShowOneHitWonders] = useState(false);
  
  // June 2022 - when Matt moved to Colorado
  const coloradoDate = new Date('2022-06-01');
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  
  // Read params from URL
  const limit = parseInt(searchParams.get('limit')) || 1000;
  const minBpm = searchParams.get('minBpm') || '';
  const maxBpm = searchParams.get('maxBpm') || '';
  const sortBy = searchParams.get('sortBy') || 'plays';
  const role = searchParams.get('role') || ''; // NEW: role filter
  
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSongId, setExpandedSongId] = useState(null); // NEW: for showing role history
  const [roleHistory, setRoleHistory] = useState({}); // NEW: cache role history data
  const [currentPlayingId, setCurrentPlayingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentList, setCurrentList] = useState([]);
  
  // Local state for filter inputs (before applying)
  const [minBpmInput, setMinBpmInput] = useState(minBpm);
  const [maxBpmInput, setMaxBpmInput] = useState(maxBpm);

  const fetchSongs = useCallback(async () => {
    try {
      setLoading(true);
      
      let url = `${API_BASE}/songs/top?limit=${limit}&sortBy=${sortBy}`;
      
      if (minBpm) url += `&minBpm=${minBpm}`;
      if (maxBpm) url += `&maxBpm=${maxBpm}`;
      if (role) url += `&role=${role}`;
      if (isInstructorMode) url += `&show=all`;
      
      const response = await axios.get(url);
      setSongs(response.data.songs);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load songs');
      console.error('Error fetching top songs:', err);
    } finally {
      setLoading(false);
    }
  }, [limit, minBpm, maxBpm, sortBy, role, isInstructorMode]);

  useEffect(() => {
    fetchSongs();
  }, [fetchSongs]);

  // Poll playback state to detect when song finishes
  useEffect(() => {
    if (!isPlaying || !currentPlayingId || currentList.length === 0) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API_BASE}/playback/state`, { withCredentials: true });
        const state = response.data;

        // Check if the track changed or stopped
        if (!state.isPlaying || !state.track) {
          // Song finished, play next
          const currentIndex = currentList.findIndex(song => song.id === currentPlayingId);

          if (currentIndex >= 0 && currentIndex < currentList.length - 1) {
            const nextSong = currentList[currentIndex + 1];
            if (nextSong.id) {
              await playSong(nextSong.id, currentList);
            }
          } else {
            // End of list
            setIsPlaying(false);
            setCurrentPlayingId(null);
          }
        } else if (state.track) {
          // Update current playing track if changed
          const trackId = state.track.id;
          if (trackId !== currentPlayingId) {
            setCurrentPlayingId(trackId);
          }
        }
      } catch (err) {
        console.error('Error polling playback state:', err);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [isPlaying, currentPlayingId, currentList]);

  // NEW: Fetch role history for a specific song
  const fetchRoleHistory = async (songId, currentRole) => {
    if (roleHistory[`${songId}-${currentRole}`]) {
      return; // Already cached
    }
    
    try {
      let url = `${API_BASE}/songs/${songId}/role-history?role=${currentRole}`;
      if (isInstructorMode) url += `&show=all`;
      
      const response = await axios.get(url);
      setRoleHistory(prev => ({
        ...prev,
        [`${songId}-${currentRole}`]: response.data.playlists
      }));
    } catch (err) {
      console.error('Error fetching role history:', err);
    }
  };

  // NEW: Toggle expanded view for a song
  const toggleSongExpanded = (songId) => {
    if (expandedSongId === songId) {
      setExpandedSongId(null);
    } else {
      setExpandedSongId(songId);
      if (role) {
        fetchRoleHistory(songId, role);
      }
    }
  };

  const applyFilters = () => {
    setSearchParams(prev => {
      if (minBpmInput) {
        prev.set('minBpm', minBpmInput);
      } else {
        prev.delete('minBpm');
      }
      
      if (maxBpmInput) {
        prev.set('maxBpm', maxBpmInput);
      } else {
        prev.delete('maxBpm');
      }
      
      if (isInstructorMode) {
        prev.set('instructor', '');
      }
      
      return prev;
    });
  };

  const clearFilters = () => {
    setMinBpmInput('');
    setMaxBpmInput('');
    setSearchParams(prev => {
      prev.delete('minBpm');
      prev.delete('maxBpm');
      if (isInstructorMode) {
        prev.set('instructor', '');
      }
      return prev;
    });
  };

  const changeSortBy = (newSortBy) => {
    setSearchParams(prev => {
      prev.set('sortBy', newSortBy);
      if (isInstructorMode) {
        prev.set('instructor', '');
      }
      return prev;
    });
  };

  // NEW: Change role filter
  const changeRole = (newRole) => {
    setSearchParams(prev => {
      if (newRole) {
        prev.set('role', newRole);
      } else {
        prev.delete('role');
      }
      if (isInstructorMode) {
        prev.set('instructor', '');
      }
      return prev;
    });
    setExpandedSongId(null); // Reset expanded view when changing roles
    setRoleHistory({}); // Clear cached history
  };

  const playSong = async (spotifyId, songList) => {
    try {
      const trackUri = `spotify:track:${spotifyId}`;
      const response = await axios.post(`${API_BASE}/playback/play`, {
        uris: [trackUri]
      }, { withCredentials: true });
      if (response.data.success) {
        setCurrentPlayingId(spotifyId);
        setIsPlaying(true);
        setCurrentList(songList);
      }
    } catch (err) {
      console.error('Error playing song:', err);
      alert(err.response?.data?.message || 'Failed to play song');
    }
  };

  const pauseSong = async () => {
    try {
      const response = await axios.post(`${API_BASE}/playback/pause`, {}, { withCredentials: true });
      if (response.data.success) {
        setIsPlaying(false);
      }
    } catch (err) {
      console.error('Error pausing song:', err);
    }
  };

  const handlePlayPause = async (spotifyId, songList) => {
    if (currentPlayingId === spotifyId && isPlaying) {
      await pauseSong();
    } else if (currentPlayingId === spotifyId && !isPlaying) {
      // Resume current song
      try {
        const response = await axios.post(`${API_BASE}/playback/play`, {}, { withCredentials: true });
        if (response.data.success) {
          setIsPlaying(true);
        }
      } catch (err) {
        console.error('Error resuming song:', err);
      }
    } else {
      await playSong(spotifyId, songList);
    }
  };

  // Filter songs based on instructor filters
  const filterSongs = (songsToFilter) => {
    if (!songsToFilter) return songsToFilter;
    let filtered = songsToFilter;
    
    // Hide songs played in last 6 months
    if (hideRecent) {
      filtered = filtered.filter(song => {
        if (!song.last_played_date) return true;
        return new Date(song.last_played_date) < sixMonthsAgo;
      });
    }
    
    // Hide songs with > 1 play since Colorado move (June 2022)
    if (hideAfterColorado) {
      filtered = filtered.filter(song => {
        if (!song.last_played_date) return true;
        const playCount = parseInt(song.total_plays) || 0;
        if (playCount <= 1) return true;
        if (new Date(song.last_played_date) < coloradoDate) return true;
        return false;
      });
    }
    
    // One Hit Wonders - only show songs played exactly once since mid-2022
    if (showOneHitWonders) {
      filtered = filtered.filter(song => {
        const playsSinceColorado = parseInt(song.plays_since_colorado) || 0;
        return playsSinceColorado === 1;
      });
    }
    
    return filtered;
  };

  const getArtistLinks = (artists) => {
    if (!artists || artists.length === 0) return 'Unknown';
    
    return artists.map((artist, index) => (
      <span key={artist.id}>
        <a 
          href={buildUrl(`/artist/${artist.id}`)}
          className="artist-link"
        >
          {artist.name}
        </a>
        {index < artists.length - 1 && ', '}
      </span>
    ));
  };

  // NEW: Get the label for the current filter
  const getRoleLabel = () => {
    if (!role) return 'All Songs';
    const labels = {
      opener: 'Openers',
      slow: 'Slow Songs',
      sprint: 'Sprint Songs',
      closer: 'Closers'
    };
    return labels[role] || 'All Songs';
  };

  // NEW: Get the count to display based on current role filter
  const getDisplayCount = (song) => {
    if (role === 'opener') return song.opener_count;
    if (role === 'slow') return song.slow_song_count;
    if (role === 'sprint') return song.sprint_count;
    if (role === 'closer') return song.closer_count;
    return song.total_plays; // Default: show total plays
  };

  if (error) {
    return (
      <div className="top-songs error">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="top-songs">
      <div className="top-songs-header">
        <h1>🏆 {getRoleLabel()}</h1>
        <p className="subtitle">Top {limit} {role ? `${getRoleLabel().toLowerCase()} from` : 'songs from'} your cycling classes</p>
      </div>

      {isInstructorMode && (
        <div className="filters-section">
          {/* NEW: Role Filter */}
          <div className="role-filter">
            <label>Filter by Role:</label>
            <button
              className={`btn-role ${!role ? 'active' : ''}`}
              onClick={() => changeRole('')}
            >
              All Songs
            </button>
            <button
              className={`btn-role opener ${role === 'opener' ? 'active' : ''}`}
              onClick={() => changeRole('opener')}
            >
              Openers
            </button>
            <button
              className={`btn-role slow ${role === 'slow' ? 'active' : ''}`}
              onClick={() => changeRole('slow')}
            >
              Slow Songs
            </button>
            <button
              className={`btn-role sprint ${role === 'sprint' ? 'active' : ''}`}
              onClick={() => changeRole('sprint')}
            >
              Sprint
            </button>
            <button
              className={`btn-role closer ${role === 'closer' ? 'active' : ''}`}
              onClick={() => changeRole('closer')}
            >
              Closers
            </button>
          </div>

          <div className="bpm-filter">
            <label>BPM Range:</label>
            <input
              type="number"
              placeholder="Min"
              value={minBpmInput}
              onChange={(e) => setMinBpmInput(e.target.value)}
              className="bpm-input"
              min="0"
              max="200"
            />
            <span className="to-label">to</span>
            <input
              type="number"
              placeholder="Max"
              value={maxBpmInput}
              onChange={(e) => setMaxBpmInput(e.target.value)}
              className="bpm-input"
              min="0"
              max="200"
            />
            <button onClick={applyFilters} className="btn-apply">
              Apply
            </button>
            {(minBpm || maxBpm) && (
              <button onClick={clearFilters} className="btn-clear">
                Clear
              </button>
            )}
          </div>

          <div className="sort-section">
            <label>Sort by:</label>
            <button
              className={`btn-sort ${sortBy === 'plays' ? 'active' : ''}`}
              onClick={() => changeSortBy('plays')}
            >
              {role ? 'Uses' : 'Plays'}
            </button>
            <button
              className={`btn-sort ${sortBy === 'bpm' ? 'active' : ''}`}
              onClick={() => changeSortBy('bpm')}
            >
              BPM
            </button>
          </div>

          {user?.isInstructor && (
            <div className="instructor-filters">
              <label className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={hideRecent}
                  onChange={(e) => setHideRecent(e.target.checked)}
                />
                Hide Recent
              </label>
              <label className="filter-checkbox hide-colorado">
                <input 
                  type="checkbox" 
                  checked={hideAfterColorado} 
                  onChange={(e) => setHideAfterColorado(e.target.checked)}
                />
                Colorado: Songs not played since 2022
              </label>
              <label className="filter-checkbox one-hit-wonders">
                <input 
                  type="checkbox" 
                  checked={showOneHitWonders} 
                  onChange={(e) => setShowOneHitWonders(e.target.checked)}
                />
                Colorado: Songs played only once since 2022
              </label>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading songs...</p>
        </div>
      )}

      {!loading && filterSongs(songs).length === 0 && (
        <div className="no-results">
          <p>No songs found matching your filters.</p>
        </div>
      )}

      {!loading && filterSongs(songs).length > 0 && (
        <div className="songs-table-container">
          <table className="songs-table">
            <thead>
              <tr>
                <th className="col-play"></th>
                <th className="col-rank">#</th>
                <th className="col-song">SONG</th>
                <th className="col-artists">ARTISTS</th>
                <th className="col-bpm">BPM</th>
                <th className="col-plays">{role ? 'USES' : 'PLAYS'}</th>
                <th className="col-last">LAST PLAYED</th>
                {role && <th className="col-expand"></th>}
              </tr>
            </thead>
            <tbody>
              {filterSongs(songs).map((song, index) => (
                <React.Fragment key={song.id}>
                  <tr>
                    <td className="col-play">
                      {song.id && (
                        <button
                          className="play-btn"
                          onClick={() => handlePlayPause(song.id, filterSongs(songs))}
                        >
                          {currentPlayingId === song.id && isPlaying ? '⏸' : '▶'}
                        </button>
                      )}
                    </td>
                    <td className="col-rank">{index + 1}</td>
                    <td className="col-song">
                      {song.spotify_url ? (
                        <a 
                          href={buildUrl(`/song/${song.id}`)}
                          className="song-link"
                        >
                          {song.name}
                        </a>
                      ) : (
                        <span className="song-name">{song.name}</span>
                      )}
                    </td>
                    <td className="col-artists">
                      {getArtistLinks(song.artists)}
                    </td>
                    <td className="col-bpm instructor-bpm">
                      {song.bpm || ' '}
                    </td>
                    <td className="col-plays">
                      {getDisplayCount(song)}
                    </td>
                    <td className="col-last">
                        {song.last_played_date ? formatShortDate(song.last_played_date) : 'Never'}
                    </td>
                    {role && (
                      <td className="col-expand">
                        <button 
                          className="btn-expand"
                          onClick={() => toggleSongExpanded(song.id)}
                          title="Show playlists"
                        >
                          {expandedSongId === song.id ? '▼' : '▶'}
                        </button>
                      </td>
                    )}
                  </tr>
                  {role && expandedSongId === song.id && (
                    <tr className="expanded-row">
                      <td colSpan={role ? 7 : 6}>
                        <div className="playlist-history">
                          <h4>Playlists where used as {getRoleLabel().slice(0, -1).toLowerCase()}:</h4>
                          {roleHistory[`${song.id}-${role}`] ? (
                            roleHistory[`${song.id}-${role}`].length > 0 ? (
                              <ul className="playlist-history-list">
                                {roleHistory[`${song.id}-${role}`].map(playlist => (
                                  <li key={playlist.id}>
                                    <a href={buildUrl(`/playlist/${playlist.id}`)}>
                                      {formatShortDate(playlist.playlist_date)}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="no-history">No playlists found</p>
                            )
                          ) : (
                            <p className="loading-history">Loading...</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="top-songs-actions">
        <a href={buildUrl('/')} className="btn-back">← Back to All Playlists</a>
      </div>
    </div>
  );
}

export default TopSongs;